<template>
  <div class="page">
    <div class="bg-grid" />
    <TopBar />
    <ProjectNav :project="project" />

    <main class="content">
      <div class="page-header">
        <div class="header-left">
          <SectionHeader label="Eval Runs" :meta="`${eval_runs.length} runs`" />
          <span class="threshold-badge">THRESHOLD: {{ score_threshold }}%</span>
        </div>
        <button
          class="trigger-btn"
          :disabled="triggering || !has_pipeline"
          @click="triggerEval"
        >
          {{ triggering ? "QUEUING..." : "RUN EVAL" }}
        </button>
      </div>

      <div v-if="message" class="flash-msg" :class="messageType">
        {{ message }}
      </div>

      <div v-if="eval_runs.length === 0" class="empty-state">
        <p class="empty-title">No eval runs yet</p>
        <p class="empty-body">Run an eval to measure how well the pipeline performs against labeled feedback data.</p>
      </div>

      <div v-else class="eval-table">
        <div class="table-header">
          <span class="col-status">STATUS</span>
          <span class="col-score">SCORE</span>
          <span class="col-judge">JUDGE</span>
          <span class="col-cases">CASES</span>
          <span class="col-trigger">TRIGGERED BY</span>
          <span class="col-date">DATE</span>
        </div>
        <div
          v-for="run in eval_runs"
          :key="run.id"
          class="table-row"
          :class="{ clickable: run.status === 'complete' }"
          @click="run.status === 'complete' ? selectRun(run) : null"
        >
          <span class="col-status">
            <span class="dot" :class="statusColor(run.status)" />
            <span class="status-text">{{ run.status }}</span>
          </span>
          <span class="col-score">
            <ScoreBar v-if="run.status === 'complete'" :score="run.score" />
            <span v-else class="score-pending">—</span>
          </span>
          <span class="col-judge">
            <span v-if="run.status === 'complete'" class="judge-score">
              {{ run.avg_judge_score != null ? run.avg_judge_score.toFixed(1) : '—' }}
            </span>
            <span v-else class="score-pending">—</span>
          </span>
          <span class="col-cases">
            <span class="cases-text">{{ run.correct_count }}/{{ run.total_cases }}</span>
          </span>
          <span class="col-trigger">
            <span class="trigger-badge" :class="triggerClass(run.triggered_by)">{{ run.triggered_by }}</span>
          </span>
          <span class="col-date date-text">{{ formatDate(run.created_at) }}</span>
        </div>
      </div>

      <!-- Detail panel -->
      <div v-if="selectedRun" class="detail-panel">
        <div class="detail-header">
          <span class="detail-title">EVAL DETAIL — RUN #{{ selectedRun.id }}</span>
          <button class="close-btn" @click="selectedRun = null">✕</button>
        </div>
        <div v-if="detailLoading" class="detail-loading">Loading…</div>
        <div v-else-if="detailResults.length === 0" class="detail-empty">No results found.</div>
        <div v-else class="detail-results">
          <div v-for="(r, i) in detailResults" :key="i" class="result-card" :class="{ correct: r.correct, incorrect: !r.correct }">
            <div class="result-row">
              <span class="result-label">EXPECTED</span>
              <span class="result-value">{{ JSON.stringify(r.expected_output) }}</span>
            </div>
            <div class="result-row">
              <span class="result-label">ACTUAL</span>
              <span class="result-value">{{ JSON.stringify(r.actual_output) }}</span>
            </div>
            <div class="result-row">
              <span class="result-label">JUDGE SCORE</span>
              <span class="result-value">{{ r.judge_score?.toFixed(1) ?? '—' }}</span>
            </div>
            <div v-if="r.judge_reasoning" class="result-row">
              <span class="result-label">REASONING</span>
              <span class="result-value reasoning">{{ r.judge_reasoning }}</span>
            </div>
            <div v-if="r.failed_dimensions?.length" class="result-row">
              <span class="result-label">FAILED</span>
              <span class="result-value">{{ r.failed_dimensions.join(', ') }}</span>
            </div>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script setup>
import { ref } from "vue"
import { router } from "@inertiajs/vue3"
import TopBar from "../../components/TopBar.vue"
import ProjectNav from "../../components/ProjectNav.vue"
import SectionHeader from "../../components/SectionHeader.vue"
import ScoreBar from "../../components/ScoreBar.vue"

const props = defineProps({
  project:          { type: Object,  required: true },
  eval_runs:        { type: Array,   default: () => [] },
  has_pipeline:     { type: Boolean, default: false },
  score_threshold:  { type: Number,  default: 70.0 },
})

const triggering    = ref(false)
const message       = ref(null)
const messageType   = ref("info")
const selectedRun   = ref(null)
const detailLoading = ref(false)
const detailResults = ref([])

async function triggerEval() {
  triggering.value  = true
  message.value     = null
  const token = document.querySelector("meta[name='csrf-token']")?.content
  try {
    const res  = await fetch(`/api/v1/projects/${props.project.uuid}/eval`, {
      method:  "POST",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
    })
    const data = await res.json()
    if (res.ok) {
      message.value     = `Eval queued — job ${data.job_id}`
      messageType.value = "success"
      setTimeout(() => router.reload(), 2000)
    } else {
      message.value     = data.message ?? "Failed to trigger eval"
      messageType.value = "error"
    }
  } catch {
    message.value     = "Network error — could not trigger eval"
    messageType.value = "error"
  } finally {
    triggering.value = false
  }
}

async function selectRun(run) {
  selectedRun.value   = run
  detailLoading.value = true
  detailResults.value = []
  try {
    const res  = await fetch(`/api/v1/projects/${props.project.uuid}/evals/${run.id}`)
    const data = await res.json()
    detailResults.value = data?.eval_run?.results ?? []
  } catch {
    detailResults.value = []
  } finally {
    detailLoading.value = false
  }
}

function statusColor(status) {
  const map = { complete: "green", failed: "red", running: "yellow", queued: "yellow" }
  return map[status] ?? "dim"
}

function triggerClass(triggeredBy) {
  if (triggeredBy === "threshold") return "auto"
  if (triggeredBy === "orchestrator") return "verification"
  if (triggeredBy === "feedback_threshold") return "feedback"
  return "manual"
}

function formatDate(dateStr) {
  if (!dateStr) return "—"
  return new Date(dateStr).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "2-digit" })
}
</script>

<style scoped>
.page {
  min-height: 100vh;
  background-color: #0a0a0b;
  position: relative;
}

.bg-grid {
  position: fixed;
  inset: 0;
  pointer-events: none;
  z-index: 0;
  background-image:
    linear-gradient(rgba(245, 166, 35, 0.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(245, 166, 35, 0.03) 1px, transparent 1px);
  background-size: 40px 40px;
}

.content {
  position: relative;
  z-index: 1;
  padding: 20px 24px;
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.threshold-badge {
  font-family: var(--font-mono);
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.8px;
  padding: 3px 8px;
  border-radius: 3px;
  background: rgba(96, 165, 250, 0.12);
  color: #60a5fa;
  border: 1px solid rgba(96, 165, 250, 0.2);
}

.trigger-btn {
  padding: 8px 18px;
  background: #f5a623;
  border: none;
  border-radius: 4px;
  font-family: var(--font-mono);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 1.2px;
  color: #0a0a0b;
  cursor: pointer;
  transition: opacity 0.2s;
}

.trigger-btn:hover:not(:disabled) { opacity: 0.85; }
.trigger-btn:disabled { opacity: 0.4; cursor: not-allowed; }

.flash-msg {
  padding: 10px 16px;
  border-radius: 4px;
  font-family: var(--font-mono);
  font-size: 11px;
  margin-bottom: 16px;
}

.flash-msg.success { background: rgba(74, 222, 128, 0.12); color: #4ade80; border: 1px solid rgba(74, 222, 128, 0.2); }
.flash-msg.error   { background: rgba(248, 113, 113, 0.12); color: #f87171; border: 1px solid rgba(248, 113, 113, 0.2); }
.flash-msg.info    { background: rgba(96, 165, 250, 0.12); color: #60a5fa; border: 1px solid rgba(96, 165, 250, 0.2); }

.empty-state {
  padding: 48px;
  text-align: center;
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.025);
}

.empty-title {
  font-family: var(--font-display);
  font-size: 16px;
  font-weight: 700;
  color: #e8e6e3;
  margin: 0 0 8px;
}

.empty-body {
  font-family: var(--font-mono);
  font-size: 12px;
  color: rgba(232, 230, 227, 0.45);
  margin: 0;
}

.eval-table {
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 8px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.025);
  margin-bottom: 20px;
}

.table-header {
  display: grid;
  grid-template-columns: 100px 140px 70px 80px 130px 1fr;
  padding: 10px 18px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  font-family: var(--font-mono);
  font-size: 9px;
  font-weight: 500;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: rgba(232, 230, 227, 0.25);
}

.table-row {
  display: grid;
  grid-template-columns: 100px 140px 70px 80px 130px 1fr;
  align-items: center;
  padding: 12px 18px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  transition: background 0.15s;
}

.table-row:hover { background: rgba(245, 166, 35, 0.06); }
.table-row.clickable { cursor: pointer; }

.col-status {
  display: flex;
  align-items: center;
  gap: 7px;
}

.dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
}

.dot.green  { background: #4ade80; box-shadow: 0 0 6px rgba(74, 222, 128, 0.6); }
.dot.yellow { background: #fbbf24; box-shadow: 0 0 6px rgba(251, 191, 36, 0.6); }
.dot.red    { background: #f87171; box-shadow: 0 0 6px rgba(248, 113, 113, 0.6); }
.dot.dim    { background: rgba(255, 255, 255, 0.2); }

.status-text {
  font-family: var(--font-mono);
  font-size: 11px;
  color: #e8e6e3;
  text-transform: capitalize;
}

.score-pending,
.cases-text,
.date-text,
.judge-score {
  font-family: var(--font-mono);
  font-size: 11px;
  color: rgba(232, 230, 227, 0.45);
}

.trigger-badge {
  font-family: var(--font-mono);
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  padding: 2px 7px;
  border-radius: 3px;
}

.trigger-badge.manual       { background: rgba(255, 255, 255, 0.06); color: rgba(232, 230, 227, 0.45); }
.trigger-badge.auto         { background: rgba(251, 191, 36, 0.15); color: #fbbf24; }
.trigger-badge.verification { background: rgba(249, 115, 22, 0.15); color: #fb923c; }
.trigger-badge.feedback     { background: rgba(167, 139, 250, 0.15); color: #a78bfa; }

/* ── Detail panel ── */
.detail-panel {
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.02);
  overflow: hidden;
}

.detail-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 18px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.detail-title {
  font-family: var(--font-mono);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 1.2px;
  color: rgba(232, 230, 227, 0.45);
}

.close-btn {
  background: none;
  border: none;
  color: rgba(232, 230, 227, 0.3);
  font-size: 14px;
  cursor: pointer;
  padding: 0;
  transition: color 0.15s;
}

.close-btn:hover { color: #e8e6e3; }

.detail-loading,
.detail-empty {
  padding: 32px;
  text-align: center;
  font-family: var(--font-mono);
  font-size: 12px;
  color: rgba(232, 230, 227, 0.25);
}

.detail-results {
  padding: 16px 18px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: 500px;
  overflow-y: auto;
}

.result-card {
  padding: 12px 14px;
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.06);
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.result-card.correct   { border-color: rgba(74, 222, 128, 0.2); background: rgba(74, 222, 128, 0.04); }
.result-card.incorrect { border-color: rgba(248, 113, 113, 0.2); background: rgba(248, 113, 113, 0.04); }

.result-row {
  display: flex;
  gap: 12px;
  align-items: flex-start;
}

.result-label {
  font-family: var(--font-mono);
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 1px;
  color: rgba(232, 230, 227, 0.25);
  min-width: 90px;
  padding-top: 1px;
}

.result-value {
  font-family: var(--font-mono);
  font-size: 11px;
  color: rgba(232, 230, 227, 0.7);
  flex: 1;
  word-break: break-word;
}

.result-value.reasoning {
  color: rgba(232, 230, 227, 0.5);
  font-style: italic;
}
</style>
